<script setup lang="ts">
import { nextTick, onMounted, ref, toValue, watch } from 'vue'
import * as echarts from 'echarts'
import { useWindowSize, watchThrottled } from '@vueuse/core'
import type { ECharts, EChartsOption } from 'echarts'
import { wait } from '@/utils/util'
import { sendErrorLog } from '@/api/chatApi'

const props = defineProps<{
  options: EChartsOption
}>()

const emit = defineEmits<{
  (e: 'init', data: ECharts): void
}>()

const echartsRef = ref()
const { width } = useWindowSize()
let echartsInstance: ECharts | undefined

async function setOption() {
  await wait()
  if (!echartsInstance) {
    return
  }
  let i = 0
  let errorMessage
  let success = false
  while (i < 20) {
    try {
      echartsInstance.setOption(toValue(props.options))
      console.log(`echarts第${i + 1}次加载成功`)
      success = true
      break
    } catch (e: any) {
      console.error(`echarts第${i}次加载错误:${e.message}`)
      errorMessage = `echarts加载失败：${e.message};option:${JSON.stringify(props.options)}`
      i++
      await sleep(100)
    }
  }

  if (!success) {
    sendErrorLog('', '', 'ECHARTS', errorMessage ?? '')
  }
}

function sleep(time: number) {
  return new Promise(function (resolve) {
    setTimeout(resolve, time)
  })
}

function resize() {
  echartsInstance?.resize()
}

watch(width, () => resize())
watchThrottled(
  () => props.options,
  () => setOption(),
  { deep: true, throttle: 500 }
)

onMounted(() => {
  echartsInstance = echarts.init(echartsRef.value, null, { renderer: 'svg' })
  emit('init', echartsInstance)
  setOption()
})

defineExpose({
  getEchartsInstance() {
    return echartsInstance
  },
  resize,
  setOption,
})
</script>

<template>
  <div class="echarts" ref="echartsRef" />
</template>

<style scoped lang="scss">
.echarts {
  height: 100%;
  width: 100%;
}
</style>
